import { Buffer } from 'buffer';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { initializeApp } from './libs/init/exec.init';
import { canonicalizeAuthTrailingSlash } from './shared/routerUtils/canonicalizeAuthTrailingSlash';
import { routes } from './routing/routes';
import { getAppBasename } from './hooks/useAppRouter/getAppBasename';
import {
  registerChunkPreloadErrorHandler,
  setChunkReloadActiveCallGuard,
} from './components/global/errorBoundary/chunkReload';
import { getIsCallActive } from './features/call';

// Some dependencies (e.g. `isemail`) expect a global `Buffer`. The `buffer`
// alias in `vite.config.ts` provides the module; expose it globally before any
// app code that relies on it runs.
globalThis.Buffer ??= Buffer;

// Skip stale-chunk reload recovery while a call is live so a recoverable
// failure does not tear down the consultation; the retry + boundary fallback
// chain handles it instead. Registered before the preload handler below.
setChunkReloadActiveCallGuard(getIsCallActive);

// Recover from stale-deploy chunk failures (rotated asset hashes) via a
// one-time reload before they surface as uncaught errors. Registered first so
// the listener is active for any dynamic import during init or mount.
registerChunkPreloadErrorHandler();

/**
 * Client entry for the Vite SPA: normalize auth URLs, run app init, then mount
 * React Router 7 synchronously (pure CSR).
 */
canonicalizeAuthTrailingSlash();

await initializeApp();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element');
}

const router = createBrowserRouter(routes, { basename: getAppBasename() });

createRoot(rootElement).render(<RouterProvider router={router} />);
